import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { VideoPlayer } from '@bloomberg/media-ui-av';
import { classes, IconButton, useSlug } from '@bloomberg/media-ui-core';
import { ExitThin, Play } from '@bloomberg/media-ui-icons';
import {
  BannerCloseReason,
  BannerDataBreakingNewsLiveStream,
} from 'src/types/index';

import { TranslatedMessage } from '../utils/translate-messages';

import styles from './BreakingNewsLiveStream.module.css';

export type BreakingNewsLiveStreamProps = BannerDataBreakingNewsLiveStream & {
  onClose?: (reason: BannerCloseReason) => void;
  desktopBreakpointClassName?: string;
  trackingFunction?: (eventAction: string, eventLabel: string) => void;
  VideoPlayerComponent?: React.ReactNode;
};

export const BreakingNewsLiveStream = React.forwardRef<
  HTMLDivElement,
  BreakingNewsLiveStreamProps
>(function BreakingNewsLiveStreamComponent(
  props: BreakingNewsLiveStreamProps,
  ref
) {
  const {
    imageUrl,
    region,
    streamId,
    theme,
    title,
    url = '',
    onClose = () => {},
    desktopBreakpointClassName,
    trackingFunction = () => {},
    VideoPlayerComponent,
  } = props;

  const playerConfig = useMemo(
    () => ({
      options: {
        id: streamId,
        live: true,
        poster: imageUrl,
        module_ad_player: 'disabled',
        hidePlayerControls: true,
      },
      autoplay: true,
      controls: false,
      video_autoplay_on_page: true,
      use_google_tag_manager: true,
      start_muted: true,
      useCustomBufferingAnimation: streamId === 'QT_MINI',
    }),
    [streamId, imageUrl]
  );

  const [closed, setClosed] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const player = useRef(null);

  const cx = classes([
    styles.breakingNewsLiveStreamContainer,
    closed && styles.closed,
  ]);
  const componentName = useSlug('breaking-news-live-stream');

  useEffect(() => {
    trackingFunction('impression', `breakingNewsLiveStream_${title}`);
  }, []);

  const onClick = useCallback(() => {
    trackingFunction('click', `breakingNewsLiveStream_${title}`);
  }, [title]);

  const handleExit = useCallback((reason: BannerCloseReason) => {
    return function handler(_: React.MouseEvent<HTMLButtonElement>) {
      onClose(reason);
      trackingFunction('click', 'breakingNewsLiveStream_close');
      setClosed(true);
    };
  }, []);

  if (!title) return null;

  return (
    <div
      className={cx}
      data-component={componentName}
      ref={ref}
      data-theme={theme}
    >
      <div className={classes([styles.content, desktopBreakpointClassName])}>
        <div className={styles.playerContainer}>
          <a href={url} tabIndex={-1} onClick={onClick}>
            {VideoPlayerComponent ? (
              <>{VideoPlayerComponent}</>
            ) : (
              <VideoPlayer {...playerConfig} playerRef={player} />
            )}

            <div data-streamid={streamId} data-region={region} />
          </a>
        </div>
        <div className={styles.textContainer}>
          <a
            className={styles.liveNowLink}
            href={url}
            tabIndex={-1}
            onClick={onClick}
          >
            <TranslatedMessage
              id="media-ui-nav_livenow"
              defaultMessage="LIVE NOW"
            />
          </a>
          <a className={styles.titleLink} href={url} onClick={onClick}>
            <div className={styles.title}>
              {theme === 'QT' ? (
                <TranslatedMessage
                  id="media-ui-nav_quicktake"
                  defaultMessage="<prefix>Quicktake:</prefix> {title}"
                  values={{
                    title,
                    prefix: (chunks: React.ReactNode) => (
                      <span className={styles.quicktake}>{chunks}</span>
                    ),
                  }}
                />
              ) : (
                title
              )}
            </div>
          </a>
          <a href={url} onClick={onClick}>
            <div className={styles.watch}>
              <span className={styles.playButton}>
                <Play />
              </span>
              <span className={styles.watchText}>
                <TranslatedMessage
                  id="media-ui-nav_watch"
                  defaultMessage="Watch"
                />
              </span>
            </div>
          </a>
        </div>
        <IconButton
          onClick={handleExit('ExitClick')}
          className={styles.iconButton}
        >
          <ExitThin />
        </IconButton>
      </div>
    </div>
  );
});
